import { DAPI_HOST, REVIEW_LIMIT } from '../../config';
import { apiGetJson, getDapiHost, onException } from './index';
import { locationResponseFormatter } from '../../reducers/formatters/location';
import { ORIGIN_REACT_MOBILE_APP, PATIENT_TYPE_KIDS } from '../../constants';
import { isSolvPartner } from '../util/location';
import { isEmptyArray } from '../util/array';

const getLocationUrl = (locationId, origin = ORIGIN_REACT_MOBILE_APP) =>
  `${DAPI_HOST}/v2/locations/${locationId}?origin=${origin}`;

const LOCATION_FIELDS = [
  'image_links',
  'appointment_dates',
  'recent_bookings',
  'scores',
  'hours',
  'adjectives',
  'attributes',
  'services',
  'services_obj',
  'timezone',
  'groups',
  'special_operating_hours',
  'hours_default',
  'payment_account_id',
  'wait_list_count',
  'accepted_insurance_types',
  'accreditations',
  'fax',
  'display_name_primary',
  'display_name_secondary',
  'display_name_alternate',
  'accepted_insurances',
  'accepted_payment_methods',
  'time_zone',
  'category',
  'id',
  'name',
  'brand_name',
  'brand_url',
  'address',
  'subpremise',
  'city',
  'state',
  'zip_code',
  'phone',
  'lat_long',
  'source',
  'is_bookable',
  'is_viewable',
  'is_solv_partner',
  'is_in_marketplace',
  'appointment_slots',
  'appointment_interval',
  'booking_url',
  'distance_from_current_location',
  'active',
  'npi',
  'geo',
  'email',
  'google_place_id',
  'status',
  'website',
  'last_walkin_appointment_date',
  'disable_reservations_until',
  'appointment_interval_reservations',
  'tracking_properties',
  'rating_solv',
  'rating_solv_count',
  'rating_non_solv',
  'rating_non_solv_count',
  'is_test_location',
  'is_paperwork_enabled',
  'is_telemed',
  'is_tokbox_enabled',
  'telemed_sla',
  'google_review_source',
  'has_next_in_line',
  'has_provider_schedule',
  'is_mobile_check_in_enabled',
  'is_uber_enabled',
  'review_flow_type',
  'live_date',
  'offboarded_date',
  'contract_signed_date',
  'google_feature_id',
  'ucl_id',
  'description_variation_test',
  'last_verified_on',
  'inside_of',
  'service_prices',
  'display_address',
  'rating_wait_time',
  'rating_bedside_manner',
  'rating_staff_friendliness',
  'rating_facility_cleanliness',
  'rating_quality_of_care',
  'rating_wait_time_count',
  'rating_bedside_manner_count',
  'rating_staff_friendliness_count',
  'rating_facility_cleanliness_count',
  'rating_quality_of_care_count',
  'platform_type',
  'custom_description',
  'year_opened',
  'neighborhoods',
  'cross_streets',
  'parking_details',
  'ownership',
  'hospital_affiliations',
  'provider_names',
  'accepted_insurers',
  'specialties',
  'market',
  'brands',
];

const getSolvAndUclMergedLocationUrl = (locationId, shouldUseCache = true) =>
  `${getDapiHost(shouldUseCache)}/v4/locations/${locationId}?origin=${ORIGIN_REACT_MOBILE_APP}` +
  `&fields=${LOCATION_FIELDS.join(',')}`;

const getLocationSlotsUrl = locationId =>
  `${DAPI_HOST}/v4/locations/${locationId}/slots?origin=${ORIGIN_REACT_MOBILE_APP}`;

const getLocationSlotsOnDateUrl = (locationId, onDate) =>
  `${DAPI_HOST}/v1/locations/${locationId}/slots?on_date=${onDate}&origin=${ORIGIN_REACT_MOBILE_APP}`;

const getNearbyLocationsUrl = ({
  locationId,
  lat,
  lng,
  isSolvPartner,
  filterByBrand,
  shouldUseCache = true,
} = {}) => {
  const baseUrl = `${getDapiHost(shouldUseCache)}/v4/locations/nearby`;
  let queryString = '?';
  if (lat && lng) {
    queryString += `lat=${lat}&lng=${lng}`;
  } else if (locationId) {
    queryString += `current_location_id=${locationId}`;
  }

  if (isSolvPartner) queryString += `&is_solv_partner=${isSolvPartner}`;
  if (filterByBrand) queryString += `&filter_by_brand=${filterByBrand}`;

  return baseUrl + queryString;
};

const compareSolvPartnerForSort = (locationA, locationB) => {
  if (isSolvPartner(locationA) && !isSolvPartner(locationB)) {
    return -1;
  }

  if (isSolvPartner(locationB) && !isSolvPartner(locationA)) {
    return 1;
  }

  return locationA.distanceFromCurrentLocation - locationB.distanceFromCurrentLocation;
};

const getGroupLocationsUrl = groupId =>
  `${DAPI_HOST}/v2/locations?group_ids=${groupId}&origin=${ORIGIN_REACT_MOBILE_APP}` +
  '&filters=locations.is_test_location:false;';

const getLocationsCityUrl = (stateCode, cityName) =>
  `${DAPI_HOST}/v2/locations?filters=locations.state:${stateCode};locations.city:${cityName}`;

const getReviewsUrl = (locationId, page = 1) =>
  `${DAPI_HOST}/v1/locations/${locationId}/reviews?limit=${REVIEW_LIMIT}&page=${page}`;

const getAdjectivesUrl = (locationId, page = 1) =>
  `${DAPI_HOST}/v1/locations/${locationId}/adjectives?limit=${REVIEW_LIMIT}&page=${page}`;

const getGroupTelemedLocations = groupIds =>
  `${DAPI_HOST}/v3/locations?group_ids=${groupIds}&` +
  'filters=locations.is_telemed:true;location_features.is_tokbox_enabled:true;' +
  'locations.is_bookable:true;locations.is_solv_partner:true';

const getTraditionalLocations = latLong =>
  `${DAPI_HOST}/v3/locations?` +
  'filters=locations.is_solv_partner:true;locations.is_test_location:false;' +
  'locations.is_bookable:true;locations.platform_type:traditional' +
  `&lat=${latLong.latitude}&long=${latLong.longitude}&radius=15`;

const getLocationListByIds = (dispatch, locationIds, onSuccess, onError) => {
  const promises = [];
  locationIds.forEach(locationId => promises.push(apiGetJson(getLocationUrl(locationId))));
  Promise.all(promises)
    .then(responses => {
      const locations = {};
      responses.forEach(json => {
        if (json && json.errors && json.errors.length) {
          return dispatch(onError(json.errors[0].description));
        }

        if (json && !json.data) {
          return dispatch(onError('Invalid response: no data.'));
        }

        locations[json.data.id] = locationResponseFormatter(json.data);
        return null;
      });
      dispatch(onSuccess(locations));
    })
    .catch(e => onException(e, dispatch, onError));
};

const getTelemedLocationUrl = ({ state, groupIds, patientType }) => {
  const BASE_URL = '/v4/locations';
  const params = [`fields=${LOCATION_FIELDS.join(',')}`];
  const filters = [
    'locations.is_telemed:true',
    'location_features.is_tokbox_enabled:true',
    'locations.is_viewable:true',
  ];
  if (groupIds) params.push(`group_ids=${groupIds.join(',')}`);
  if (state) filters.push(`locations.state:${state}`);
  if (patientType) {
    filters.push(
      `location_attributes_flat.${
        patientType === PATIENT_TYPE_KIDS
          ? 'is_pediatric_patients_accepted'
          : 'is_adult_patients_accepted'
      }:true`
    );
  }

  params.push(`filters=${filters.join(';')}`);

  return `${DAPI_HOST}${BASE_URL}?${params.join('&')}`;
};

const getLocationsListUrl = locationIds =>
  `${DAPI_HOST}/v2/locations?origin=${ORIGIN_REACT_MOBILE_APP}&filters=` +
  'locations.is_bookable:true;locations.is_solv_partner:true;' +
  `locations.id_hash:${locationIds.join(',')}`;

const getFavoriteLocationsUrl = accountId =>
  `${DAPI_HOST}/v1/locations/favorite-locations?account_id=${accountId}&fields=${LOCATION_FIELDS.join(
    ','
  )}`;

const getSolvRatingsByLocationsUrl = locationId =>
  `${DAPI_HOST}/v1/locations/${locationId}/solv-ratings?hide-empty-reviews=true`;

/**
 * Get the url of the next available locations near you
 * @param {Number || string} latitude
 * @param {Number || string} longitude
 * @param {string[]} providerTypes
 * @returns {string}
 */
const getNextAvailableLocationsUrl = ({ latitude, longitude }, providerTypes) =>
  `${DAPI_HOST}/v1/locations/next-available?lat=${latitude}&long=${longitude}` +
  `&provider_types=${providerTypes.join(',')}`;

export {
  compareSolvPartnerForSort,
  getAdjectivesUrl,
  getGroupLocationsUrl,
  getTelemedLocationUrl,
  getGroupTelemedLocations,
  getFavoriteLocationsUrl,
  getNextAvailableLocationsUrl,
  getLocationListByIds,
  getLocationSlotsUrl,
  getLocationSlotsOnDateUrl,
  getLocationUrl,
  getLocationsCityUrl,
  getLocationsListUrl,
  getNearbyLocationsUrl,
  getTraditionalLocations,
  getReviewsUrl,
  getSolvAndUclMergedLocationUrl,
  getSolvRatingsByLocationsUrl,
};
